<template>
  <div>
    <navbar :links="links" />
    <v-subheader
      v-if="!accessible"
      class="bg-warning mt-4"
      style="justify-content: center"
    >
      {{ $t('forbidden') }}
    </v-subheader>
    <div v-else>
      <v-list
        avatar
        :dense="$vuetify.breakpoint.width < 400"
      >
        <v-list-tile
          avatar
          class="selectable"
          :ripple="false"
        >
          <v-list-tile-avatar>
            <span v-if="$route.params.ctype === 'radio'">
              <v-icon
                v-if="!isClosingTimeRadioSpot(spot)"
                large
                class="msr--text"
              >
                queue_music
              </v-icon>
              <v-icon
                v-else
                large
                class="msr--text"
              > campaign </v-icon>
            </span>
            <!-- We have TVSpot here, not a TVDecoder, but can use the same icons. -->
            <icon-tv-decoder
              v-if="$route.params.ctype === 'tv'"
              :tvdecoder="spot"
              color="msr"
              large
            />
          </v-list-tile-avatar>
          <v-list-tile-content v-if="spot.title">
            <v-list-tile-title>
              <strong
                class="grey--text text--darken-3"
                style="white-space: initial"
              >{{ spot.title }}</strong>
            </v-list-tile-title>
            <v-list-tile-sub-title data-cy="spotdetail-category-subtitle">
              <div v-if="spot.category">
                {{ spot.category.name }}
              </div>
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
      <!-- Preview -->
      <spot-detail-preview
        v-if="spot.id"
        ref="preview"
        :key="spotLastFetched"
        :spot="spot"
        :bundle="bundle"
        :entries="entries"
        @changed-previewed-spot="(tvspot) => (previewedSpot = tvspot)"
      />
      <!-- Settings/Restricted Actions -->
      <spot-detail-toolbar
        class="mt-3"
        :bundle="bundle"
        :categories="categoriesForSpot"
        :disable-share-on-facebook-button-handler="
          disableShareOnFacebookButtonHandler
        "
        :disable-share-on-facebook-button-message="
          disableShareOnFacebookButtonMessage
        "
        :disable-share-on-facebook-button="disableShareOnFacebookButton"
        :disable-share-on-instagram-button-message="
          disableShareOnInstagramButtonMessage
        "
        :disable-share-on-instagram-button="disableShareOnInstagramButton"
        :entries="entries"
        :loading="loading"
        :permissions="permissions"
        :previewed-spot="previewedSpot"
        :settings-menu.sync="settingsMenu"
        :share-on-social-media-button-loading="busyTalkingToGraphApi"
        :show-batch-menu="!onlyRadioTagEntries"
        :show-settings-button="showSettingsButton"
        :show-share-on-social-media-button="showShareOnSocialMediaButton"
        :show-statistics-button="showStatisticsButton"
        :spot="spot"
        :updating="updating"
        @categorize="onCategoryChanged"
        @change-image-duration="openChangeImageDurationDialog()"
        @delete="openConfirmRemovalDialog()"
        @disband-bundle="askDisbandTVSpotBundle()"
        @download="downloadTvContent(previewedSpot)"
        @edithardrange="openEditHardRangeDialog()"
        @edittemplatedata="openEditTemplateDataDialog()"
        @entry-selection-overlay-toggle="onEntrySelectionOverlayToggled"
        @publish="showPublishDialog()"
        @refresh-date="openRefreshContentDateDialog()"
        @rename="openRenameDialog()"
        @replace="goToReplaceSpotPage(spot)"
        @share-on-facebook-page="chooseSpotToShareOnFacebook()"
        @share-on-instagram="chooseSpotToShareOnInstagram()"
        @tags-changed="onSpotTagsChanged"
        @toggle-tag-entries-can-be-deactivated="
          onToggleTagEntriesCanBeDeactivated
        "
        @unpublish="openUnpublishDialog()"
      />
      <!-- Loading indicator -->
      <div class="loading-indicator">
        <v-progress-circular
          v-if="loading"
          :size="50"
          indeterminate
          class="mt-5 mb-2 msr--text"
        />
      </div>
      <spot-detail-status
        :spot="spot"
        :loading="loading"
        :permissions="permissions"
        @edithardrange="openEditHardRangeDialog()"
        @edittemplatedata="openEditTemplateDataDialog()"
        @publish="showPublishDialog()"
      />
      <!-- 'No entries' -->
      <no-entries-header
        v-if="spot.published"
        :show="!!spot.id && !entries.length"
        :label="$t('noSpotEntries')"
        :show-activation-link="shouldShowAddContentToDecoderButton"
        :on-link-clicked="
          () => goToAddDecoderToSpot($route.params.ctype, $route.params.spotId)
        "
        data-cy="spotdetail-no-entries-header"
      />
      <!-- Active Entries -->
      <spot-detail-entries
        ref="activeSpotDetailEntries"
        :entries="entries"
        :spot-usage-in-stores="isTagged ? null : spot.usage_in_stores"
        active-only
        show-header
        :show-selection-checkboxes="entrySelectionOverlay.active"
        @entry-deleted="fetchContent()"
        @changed="fetchContent()"
      />
      <!-- Future Entries -->
      <spot-detail-entries
        ref="futureSpotDetailEntries"
        :entries="entries"
        :spot-usage-in-stores="spot.usage_in_stores"
        future-only
        show-header
        :show-selection-checkboxes="entrySelectionOverlay.active"
        @entry-deleted="fetchContent()"
        @changed="fetchContent()"
      />
      <!-- Add Decoder to Spot button -->
      <v-slide-y-reverse-transition>
        <v-btn
          v-if="shouldShowAddContentToDecoderButton"
          :key="spot.id"
          class="msr"
          :class="$desktopModeStyles"
          style="z-index: 2; /* Overlap mejs player */"
          fixed
          bottom
          right
          dark
          round
          data-cy="add-decoder-to-spot-btn"
          @click="
            goToAddDecoderToSpot($route.params.ctype, $route.params.spotId)
          "
        >
          <v-icon left>
            add
          </v-icon>
          {{ $t('addDecoderToSpot') }}
        </v-btn>
      </v-slide-y-reverse-transition>
      <!-- Empty placeholder to allow a bit more scrolling -->
      <div style="height: 100px" />
      <!-- Remove-Spot entirely Dialog -->
      <v-dialog
        v-model="showConfirmRemovalDialog"
        lazy
        :persistent="deleting"
        :width="$store.state.common.largeDialogWidth"
      >
        <v-card>
          <v-card-title class="pb-0">
            <v-layout
              align-center
              justify-center
            >
              <v-icon
                medium
                class="msr--text mr-2"
              >
                delete
              </v-icon>
              <h1 class="headline">
                {{ $t('deleteContent') }}
              </h1>
              <v-btn
                class="ml-auto mr-0 my-0"
                :disabled="deleting"
                flat
                icon
                :title="$t('close')"
                @click="showConfirmRemovalDialog = false"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-layout>
          </v-card-title>
          <v-card-text>
            <div class="subheading">
              {{ $t('askDeleteContent') }}
              <div class="error--text mt-2 pa-2">
                <strong>{{ $t('unpublishContentHint_2') }}</strong>
              </div>
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <btn-ok
              :loading="deleting"
              round
              @click="removeSpot()"
            />
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Edit Content Name Dialog -->
      <v-dialog
        v-model="showEditContentDialog"
        lazy
        persistent
        :width="$store.state.common.largeDialogWidth"
      >
        <v-card>
          <v-card-title class="pb-0">
            <v-layout
              row
              align-center
              justify-center
            >
              <v-icon
                medium
                class="msr--text mr-2"
              >
                edit
              </v-icon>
              <h1 class="headline">
                {{ $t('renameContent') }}
              </h1>
              <v-btn
                class="ml-auto mr-0 my-0"
                :disabled="loading"
                flat
                icon
                :title="$t('cancel')"
                @click="showEditContentDialog = false"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-layout>
          </v-card-title>
          <v-card-text class="py-0">
            <v-text-field
              ref="contentTitleInput"
              v-model="newContentTitle"
              autofocus
              :counter="titleLimit"
              :rules="[rules.minLength]"
              @keyup.esc="showEditContentDialog = false"
              @keyup.enter="renameContent(spot.ctype, spot.id, newContentTitle)"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <btn-ok
              round
              :disabled="
                newContentTitle === spot.title ||
                  newContentTitle.length < titleMinimum ||
                  newContentTitle.length > titleLimit
              "
              :loading="loading"
              @click="renameContent(spot.ctype, spot.id, newContentTitle)"
            />
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Change Image Duration Dialog -->
      <v-dialog
        v-model="showChangeImageDurationDialog"
        lazy
      >
        <v-card>
          <v-card-title class="pb-0">
            <v-layout
              align-center
              justify-center
            >
              <v-icon
                medium
                class="msr--text mr-2"
              >
                av_timer
              </v-icon>
              <h1 class="headline">
                {{ $t('changeImageDuration') }}
              </h1>
              <v-btn
                class="ml-auto mr-0 my-0"
                :disabled="loading"
                flat
                icon
                :title="$t('cancel')"
                @click="showChangeImageDurationDialog = false"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-layout>
          </v-card-title>
          <v-card-text class="pb-0">
            <p>{{ $t('changeImageDurationDialogHint') }}</p>
            <v-text-field
              v-model="imageDuration"
              type="number"
              prepend-icon="av_timer"
              :min="minImageDuration"
              :max="maxImageDuration"
              step="1"
              class="mt-1"
              box
              :label="$t('seconds')"
              :title="$t('imageDurationSecondsTooltip')"
              :rules="[rules.imageDuration]"
              style="max-width: 240px"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <btn-ok
              round
              :label="$t('apply')"
              :loading="loading"
              :disabled="
                spot.ctype !== 'tv' ||
                  !imageDuration ||
                  imageDuration < minImageDuration ||
                  imageDuration > maxImageDuration
              "
              @click="changeImageDuration(spot.id, imageDuration)"
            />
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Refresh Content Date Dialog -->
      <v-dialog
        v-model="showRefreshContentDateDialog"
        lazy
        :width="$store.state.common.largeDialogWidth"
      >
        <v-card>
          <v-card-title class="pb-0">
            <v-layout
              row
              align-center
              justify-center
            >
              <v-icon
                medium
                class="msr--text mr-2"
              >
                refresh
              </v-icon>
              <h1 class="headline">
                {{ $t('refreshContentDate') }}
              </h1>
              <v-btn
                class="ml-auto mr-0 my-0"
                :disabled="loading"
                flat
                icon
                :title="$t('cancel')"
                @click="showRefreshContentDateDialog = false"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-layout>
            <div class="mt-3 mb-2 subheading grey--text text--darken-3">
              {{ $t('refreshContentDateDialogHint') }}
            </div>
          </v-card-title>
          <v-card-actions>
            <v-spacer />
            <btn-ok
              round
              :label="$t('apply')"
              :loading="loading"
              @click="refreshContentDate(spot.ctype, spot.id)"
            />
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Edit Hard Range Dialog -->
      <edit-hard-range-dialog
        :show.sync="showEditHardRangeDialog"
        :hard-start="spot.hard_start"
        :hard-expire="spot.hard_expire"
        :show-entries-warning="spot.entries.length > 0"
        :loading="loading"
        @confirm="updateSpotHardRange"
      />
      <!-- Edit template_data Dialog -->
      <edit-template-data-dialog
        v-if="spot && spot.template && spot.template_data"
        :show.sync="showEditTemplateDataDialog"
        :tvspot="spot"
        :loading="loading"
        @edit-begin="loading = true"
        @edit-finish="
          fetchContent().finally(() => {
            loading = false;
          })
        "
      />
      <!-- Entry Selection Overlay for Batch Timing etc. -->
      <entry-selection-overlay
        v-if="entries"
        :show="entrySelectionOverlay.active"
        :entries="entries"
        :title="entrySelectionOverlay.title"
        :title-icon="entrySelectionOverlay.titleIcon"
        :mode="entrySelectionOverlay.mode"
        :loading="loading || updating"
        @aborted="entrySelectionOverlay.stop()"
        @confirmed="onEntrySelectionOverlayConfirmed"
      />
      <!-- Entry Timing Dialog -->
      <entry-timing-dialog
        :show.sync="showEntryTimingDialog"
        :entries="selectedEntries"
        :show-timeframe-controls="!isClosingTimeRadioSpot(spot)"
        @submitted="
          entrySelectionOverlay.stop();
          fetchContent();
        "
      />
      <!-- Edit OverlayData Dialog -->
      <edit-overlay-data-dialog
        :show.sync="showEditOverlayDataDialog"
        :entries="selectedEntries"
        :num-entries-overall="entries.length"
        @submitted="onEditOverlayDataChanged"
      />
      <!-- Choose Spot to share on Social Media Dialog -->
      <choose-spot-to-share-on-social-media-dialog
        v-if="spot.ctype == 'tv'"
        :show.sync="showChooseSpotToShareOnSocialMediaDialog"
        :bundle="bundle"
        :platform="targetSocialMediaPlatform"
        @chosen="onSpotToShareOnTargetSocialMediaPlatformChosen"
      />
      <!-- Share to Facebook Page Dialog -->
      <share-on-facebook-page-dialog
        v-if="spot.ctype == 'tv' && chosenSpotToShareOnSocialMedia"
        :show.sync="showShareOnFacebookDialog"
        :loading="busyTalkingToGraphApi"
        :tvspot="chosenSpotToShareOnSocialMedia"
        :pages="userManagedFacebookPages"
        :progress.sync="sharingToFacebookPagesProgress"
        @confirmed="handleShareOnFacebookConfirmed"
      />
      <!-- Share to Instagram Dialog -->
      <share-on-instagram-dialog
        v-if="spot.ctype == 'tv' && chosenSpotToShareOnSocialMedia"
        :show.sync="showShareOnInstagramDialog"
        :loading="busyTalkingToGraphApi"
        :tvspot="chosenSpotToShareOnSocialMedia"
        :pages="userManagedFacebookPagesWithConnectedInstagram"
        @confirmed="handleShareOnInstagramConfirmed"
      />
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import 'mediaelement/build/mediaelement-and-player.min.js';

import {
  urlQueryParamToBoolean,
  removeUrlQueryParamsFromCurrentRoute,
} from '@/utils.js';
import {IMAGE_DURATION_MAX, IMAGE_DURATION_MIN} from '@/mixins/imageDuration';
import {isTaggedContent} from '@/mixins/content.js';
import commonEntry from '@/mixins/commonEntry.js';
import content from '@/mixins/content.js';
import contentPublishing from '@/mixins/contentPublishing.js';
import entrySelectionOverlay from '@/mixins/entrySelectionOverlay.js';
import identifiers from '@/mixins/identifiers.js';
import shareOnSocialMedia from '@/mixins/shareOnSocialMedia';
import updateOnContentProcessed from '@/mixins/updateOnContentProcessed.js';

import BtnOk from '@/components/ButtonOk';
import ChooseSpotToShareOnSocialMediaDialog from '@/components/ChooseSpotToShareOnSocialMediaDialog';
import EditHardRangeDialog from '@/components/EditHardRangeDialog';
import EditOverlayDataDialog from '@/components/EditOverlayDataDialog';
import EditTemplateDataDialog from '@/components/EditTemplateDataDialog';
import EntrySelectionOverlay from '@/components/EntrySelectionOverlay';
import EntryTimingDialog from '@/components/EntryTimingDialog';
import IconTvDecoder from '@/components/IconTvDecoder';
import Navbar from '@/components/Navbar';
import NoEntriesHeader from '@/components/NoEntriesHeader';
import ShareOnFacebookPageDialog from '@/components/ShareOnFacebookPageDialog';
import ShareOnInstagramDialog from '@/components/ShareOnInstagramDialog';
import SpotDetailEntries from '@/components/SpotDetail/SpotDetailEntries';
import SpotDetailPreview from '@/components/SpotDetail/SpotDetailPreview';
import SpotDetailStatus from '@/components/SpotDetail/SpotDetailStatus';
import SpotDetailToolbar from '@/components/SpotDetail/SpotDetailToolbar';

const SPOTCATEGORY_ID_HERSTELLER = 3;

export default {
  components: {
    BtnOk,
    ChooseSpotToShareOnSocialMediaDialog,
    EditHardRangeDialog,
    EditOverlayDataDialog,
    EditTemplateDataDialog,
    EntrySelectionOverlay,
    EntryTimingDialog,
    IconTvDecoder,
    Navbar,
    NoEntriesHeader,
    ShareOnFacebookPageDialog,
    ShareOnInstagramDialog,
    SpotDetailEntries,
    SpotDetailPreview,
    SpotDetailStatus,
    SpotDetailToolbar,
  },
  mixins: [
    commonEntry,
    content,
    contentPublishing,
    entrySelectionOverlay,
    identifiers,
    shareOnSocialMedia,
    updateOnContentProcessed,
  ],
  data() {
    const vm = this;
    return {
      accessible: true,
      loading: false,
      updating: false,
      deleting: false,

      maxImageDuration: IMAGE_DURATION_MAX,
      minImageDuration: IMAGE_DURATION_MIN,
      imageDuration: null,

      expandEntriesIfNumStoresLessThan: 4,
      settingsMenu: false,
      showEditContentDialog: false,
      showEditHardRangeDialog: false,
      showEditOverlayDataDialog: false,
      showEditTemplateDataDialog: false,
      showEntryTimingDialog: false,
      showChangeImageDurationDialog: false,
      showRefreshContentDateDialog: false,

      links: ['dashboard', {label: 'preview', active: false}],

      content_: {
        entries: [],
      },
      /** Epoch milliseconds, used as key to re-render Spot player. */
      spotLastFetched: '0000000000000',

      // Reference currently previewed Spot for the download feature.
      previewedSpot: null,

      newContentTitle: '',
      titleMinimum: 3,
      titleLimit: 128,
      rules: {
        minLength(v) {
          return (
            v.length >= 3 ||
            vm.$t('minLengthNeeded').replace('{{ num }}', vm.titleMinimum)
          );
        },
        imageDuration: (value) => {
          if (value < this.minImageDuration) {
            return this.$t('tooShort');
          }
          if (value > this.maxImageDuration) {
            return this.$t('tooLong');
          }
          return true;
        },
      },
    };
  },
  computed: {
    onlyRadioTagEntries() {
      const numEntries = this.entries.length;
      return (
        numEntries > 0 &&
        this.entries.filter((entry) => this.isRadioTagEntry(entry)).length ==
          numEntries
      );
    },
    isTagged() {
      return isTaggedContent(this.spot);
    },
    shouldShowAddContentToDecoderButton() {
      return (
        this.spot.id &&
        this.spot.published &&
        !this.spot.expired &&
        !this.isTagged &&
        !this.entrySelectionOverlay.active
      );
    },
    categories() {
      return this.$store.state.common.categories;
    },
    defaultCategories() {
      return this.categories.filter((category) => category.is_default);
    },
    categoriesForSpot() {
      if (!this.spot || !this.spot.group) {
        return [];
      }
      const groupCategories = this.categories.filter(
        (category) =>
          !!category.group && category.group.id == this.spot.group.id
      );
      return [...this.defaultCategories, ...groupCategories];
    },
    /**
     * Whether the User can manage content for the current Spot's Group.
     */
    isGroupContentManager() {
      if (!this.spot.id || !this.$user) {
        return false;
      }
      const uploadGroupIds = this.$store.getters.uploadgroups.map(
        (group) => group.id
      );
      return uploadGroupIds.indexOf(this.spot.group.id) !== -1;
    },
    canEditTemplateData() {
      return (
        this.isGroupContentManager &&
        !!this.$userprofile &&
        !!this.$userprofile.content_creator_plan &&
        this.$userprofile.content_creator_plan.can_edit_produced_content
      );
    },
    /**
     * We'll allow to view statistics even for Spots in other Groups if
     * the User can manage content for at least one Association Group.
     * This is a common scenario in which via Group inheritance the
     * Spot of interest may be in some other Group.
     *
     * Since tagged contents are not yet usage-tracked properly, we hide
     * the button for those for now (see #2089).
     */
    showStatisticsButton() {
      if (!this.spot.id) {
        return false;
      }
      if (this.spot.tags && this.spot.tags.length > 0) {
        return false;
      }
      return (
        this.isGroupContentManager &&
        this.$store.getters.userCanSeeContentStoreStatistics
      );
    },
    showSettingsButton() {
      return this.isGroupContentManager;
    },
    contentCanBeDownloaded() {
      if (!this.$userprofile || !this.spot) {
        return false;
      }

      if (this.spot.ctype != 'tv') {
        return false;
      }

      if (
        this.spot.is_editable ||
        this.spot.category.id === SPOTCATEGORY_ID_HERSTELLER
      ) {
        return false;
      }

      const groupId = this.spot.group.id;
      for (const group of this.$userprofile.accessible_groups) {
        if (group.id !== groupId) {
          continue;
        }
        return group.can_manage_content && group.content_downloadable;
      }
      return false;
    },
    /**
     * Disallow replacing TVSpot within a bundle with mixed non-standard formats.
     *
     * Aka, if it's all standard formats (16:9, 9:16, 32:9) it can be replaced,
     * if it's a single special format like Artwall it can be replaced, but a
     * mix of those is not possible as we can't produce special formats from
     * random video input automatically.
     */
    contentCanBeReplaced() {
      if (this.spot.ctype === 'radio') {
        return true;
      }
      if (this.spot.ctype === 'tv') {
        const tvformats = this.bundle.tvspots.map((tvspot) => tvspot.tvformat);
        if (tvformats.length === 1) {
          // We expect the user to upload the exact needed pixel dimensions.
          return true;
        }
        // We do show the replacement option at this point, the option
        // may still be disabled in the SpotDetailToolbar though if we
        // have multiple tvformats, in which case we ask the user to
        // disband the bundle first so any specific handling per
        // tvformat becomes explicit.
        return true;
      }
      return false;
    },
    permissions() {
      return {
        categorize: this.isGroupContentManager,
        changeImageDuration: this.isGroupContentManager,
        delete: this.isGroupContentManager,
        disbandTVSpotBundle:
          this.isGroupContentManager &&
          this.bundle &&
          this.bundle.tvspots.length > 1,
        download: this.isGroupContentManager && this.contentCanBeDownloaded,
        editHardRange: this.isGroupContentManager,
        editTemplateData: this.canEditTemplateData,
        publish: this.isGroupContentManager,
        refreshDate: this.isGroupContentManager,
        rename: this.isGroupContentManager,
        replace: this.isGroupContentManager && this.contentCanBeReplaced,
        tag:
          this.isGroupContentManager &&
          this.$userprofile.can_use_tagging &&
          !!this.spot.id,
        unpublish: this.isGroupContentManager,
      };
    },
    numEntriesStores() {
      const storeNames = this.spot.entries.map(
        (entry) => entry.decoder.store.name
      );
      const uniqueNames = storeNames.filter((item, idx, arr) => {
        return arr.indexOf(item) === idx;
      });
      return uniqueNames.length;
    },
  },
  watch: {
    showEditContentDialog: function (newVal, oldVal) {
      if (newVal) {
        if (this.$refs.contentTitleInput) {
          this.$nextTick(() => {
            this.$refs.contentTitleInput.focus();
          });
        }
      }
    },
    /**
     * Make sure to (re-)initialize players if Spot changes.
     */
    spot: function (spot) {
      if (spot.processed) {
        this.$nextTick(() => {
          this.setupPreview();
        });
      }
    },
  },
  mounted() {
    this.fetchCategoriesIfNeeded();
    this.entrySelectionOverlay.view = 'content';

    this.loading = true;
    this.fetchContent().then(() => {
      this.loading = false;
      this.updateNavbarLinks();
      this.setInitialWrapperExpansion();
      this.handleInitialEditOverlayDataDialogOpened();
    });

    this.refetchOnContentProcessed();
  },
  methods: {
    onEditOverlayDataChanged() {
      this.entrySelectionOverlay.stop();

      // Force preview to update itself with potential new overlay_data.
      this.fetchContent();
    },
    updateSpotHardRange(opts) {
      this.loading = true;
      this.$api
        .updateContentHardRange(
          this.spot.ctype,
          this.spot.id,
          opts.newHardStart,
          opts.newHardExpire
        )
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'entryUpdatedSuccess',
            translate: true,
          });
          this.fetchContent().finally(() => {
            this.loading = false;
          });
        });
    },
    downloadTvContent(tvspot) {
      if (!tvspot) {
        return;
      }

      const vm = this;
      this.$api.getDownloadUrlForTVSpot(tvspot.id).then((response) => {
        const downloadUrl = response.data;
        const keepCharsAndNumbersRegex = /[^A-Za-z0-9]/gi;
        const filename =
          tvspot.title.replace(keepCharsAndNumbersRegex, '_') +
          '_' +
          tvspot.tvformat.name.replace(keepCharsAndNumbersRegex, '_') +
          '.mp4';

        const link = document.createElement('a');
        link.download = filename;
        link.href = downloadUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const message =
          vm.$t('contentIsBeingDownloaded') + ': ' + tvspot.tvformat.name;
        vm.$bus.$emit('alert-info', {message: message, timeout: 3000});
      });
    },
    askDisbandTVSpotBundle() {
      if (!this.bundle) {
        return;
      }

      const confirmed = confirm(this.$t('disbandTVSpotBundleQuestion'));
      if (!confirmed) {
        return;
      }

      return this.$api.disbandTVSpotBundle(this.bundle.id).then(() => {
        this.$bus.$emit('alert-success', {
          message: 'disbandTVSpotBundleSuccess',
          translate: true,
        });
        this.fetchContent();

        this.$store.commit('RESET_LAST_CONTENTS_QUERY');
        this.$store.commit('RESET_CONTENT_FILTERS');
        this.$router.push({name: 'contents'});
      });
    },
    onSpotTagsChanged() {
      this.$store.commit('SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT', true);
      this.fetchContentDebounced();
    },
    onToggleTagEntriesCanBeDeactivated() {
      // Frontend update.
      this.spot.tag_entries_can_be_deactivated =
        !this.spot.tag_entries_can_be_deactivated;

      this.updating = true;
      return this.$api
        .toggleTagEntriesCanBeDeactivated(this.spot.ident)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'genericEntryUpdateSuccess',
            translate: true,
            timeout: 1000,
          });
          this.fetchContent();
        })
        .finally(() => {
          this.updating = false;
        });
    },
    /** Upload Spot to Facebook, then refetch the Spot in here. */
    handleShareOnFacebookConfirmed(chosenPagesPayload) {
      this.onShareSpotOnFacebookConfirmed(chosenPagesPayload).then(() => {
        this.fetchContent();
      });
    },
    /** Upload Spot to Instagram, then refetch the Spot in here. */
    handleShareOnInstagramConfirmed(chosenPagePayload) {
      this.onShareSpotOnInstagramConfirmed(chosenPagePayload).then(() => {
        this.fetchContent();
      });
    },
    setupPreview() {
      if (!this.$refs.preview) {
        return;
      }
      this.$refs.preview.setupPlayer();
    },
    goToReplaceSpotPage(spot) {
      const contentId = spot.ctype === 'tv' ? this.bundle.id : spot.id;
      const path = `/spot/${spot.ctype}/${contentId}/replace`;
      this.$router.push(path);
    },
    openRenameDialog() {
      this.settingsMenu = false;
      this.newContentTitle = this.spot.title;
      this.showEditContentDialog = true;
    },
    openChangeImageDurationDialog() {
      this.settingsMenu = false;
      this.imageDuration = this.spot.duration;
      this.showChangeImageDurationDialog = true;
    },
    openRefreshContentDateDialog() {
      this.settingsMenu = false;
      this.showRefreshContentDateDialog = true;
    },
    openUnpublishDialog() {
      this.showUnpublishDialog();
      this.settingsMenu = false;
    },
    openEditHardRangeDialog() {
      this.showEditHardRangeDialog = true;
      this.settingsMenu = false;
    },
    openEditTemplateDataDialog() {
      this.showEditTemplateDataDialog = true;
      this.settingsMenu = false;
    },
    onCategoryChanged(categoryId) {
      this.changeCategory(this.spot.ctype, this.spot.id, categoryId);
      this.settingsMenu = false;
    },
    openConfirmRemovalDialog() {
      this.showConfirmRemovalDialog = true;
      this.settingsMenu = false;
    },
    /**
     * Call this before anything using entry checkboxes is called.
     * It will ensure the StoreWrappers are expanded so checkboxes are
     * visible to the user.
     */
    expandAllEntries() {
      if (this.$refs.activeSpotDetailEntries) {
        this.$refs.activeSpotDetailEntries.allExpanded = true;
      }
      if (this.$refs.futureSpotDetailEntries) {
        this.$refs.futureSpotDetailEntries.allExpanded = true;
      }
    },
    updateSpotLastFetched() {
      this.spotLastFetched = new Date().getTime().toString();
    },
    fetchContent() {
      const ctype = this.$route.params.ctype;
      const spotId = this.$route.params.spotId;

      if (!ctype || !spotId) {
        // User likely navigated to a different route quickly.
        return;
      }

      return this.$api
        .retrieveContent(ctype, spotId)
        .then((response) => {
          this.spot = response.data;
          if (this.previewedSpot == null) {
            this.previewedSpot = this.spot;
          }
          this.updateSpotLastFetched();
        })
        .catch((error) => {
          if (error.response.status === 403) {
            this.accessible = false;
            this.loading = false;
          }
        });
    },
    fetchContentDebounced: debounce(function () {
      this.fetchContent();
    }, 1000),
    onEntrySelectionOverlayToggled(mode) {
      this.expandAllEntries();
      this.entrySelectionOverlay.toggle(mode);
    },
    onEntrySelectionOverlayConfirmed(hotrotate) {
      if (this.entrySelectionOverlay.mode === 'timing') {
        this.showEntryTimingDialog = true;
      } else if (this.entrySelectionOverlay.mode === 'removing') {
        this.batchRemoveEntries(this.selectedEntries);
      } else if (this.entrySelectionOverlay.mode === 'overlayediting') {
        this.showEditOverlayDataDialog = true;
      } else if (this.entrySelectionOverlay.mode === 'hotrotating') {
        this.updating = true;
        this.batchToggleEntriesHotRotation(
          this.selectedEntries,
          hotrotate
        ).then(() => {
          this.entrySelectionOverlay.stop();
          this.fetchContent();
          this.updating = false;
        });
      }
    },
    updateNavbarLinks() {
      let spotLink = {label: this.spot.title, active: false};
      this.links = ['contents', spotLink];

      let previous = this.$store.getters.previousRoute;
      if (!previous) {
        return;
      }

      if (previous.name === 'contents') {
        this.links = ['contents', spotLink];
      } else if (previous.name === 'dashboard') {
        this.links = ['dashboard', spotLink];
      } else if (previous.name === 'decoder') {
        this.$api
          .retrieveDecoder(previous.params.ctype, previous.params.decoderId)
          .then((response) => {
            const decoder = response.data;
            const decoderLink = {
              label: decoder.description,
              path: previous.path,
            };
            this.links = ['sites', decoderLink, spotLink];
          });
      }
    },
    goToAddDecoderToSpot(ctype, spotId) {
      const path = '/spot/' + ctype + '/' + spotId + '/add';
      this.$router.push(path);
    },
    /**
     * Remove Radio- or TVSpot (and UploadImage) uploaded by the User.
     */
    removeSpot() {
      this.deleting = true;
      this.$api
        .removeSpot(this.spot.ctype, this.spot.id)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'contentDeletedSuccess',
            translate: true,
          });
          // Note: For TV we need to pass the bundle, as the content
          //   contain TVSpotBundles, not individual TVSpots.
          this.$store.commit(
            'REMOVE_CONTENT_IN_LISTS',
            this.bundle || this.spot
          );
          this.$router.push('/content');
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    batchRemoveEntries(entries) {
      this.loading = true;
      const allowedEntries = null;
      this.batchRemoveMultipleEntries(entries, allowedEntries).then(() => {
        this.loading = false;
        this.entrySelectionOverlay.stop();
        this.fetchContent();
      });
    },
    renameContent(ctype, spotId, newTitle) {
      this.loading = true;
      this.$api.renameContent(ctype, spotId, newTitle).then(() => {
        this.$bus.$emit('alert-success', {
          message: 'entryUpdatedSuccess',
          translate: true,
        });
        this.showEditContentDialog = false;
        this.fetchContent().then(() => {
          this.loading = false;
        });
      });
    },
    changeImageDuration(tvspotId, duration) {
      this.loading = true;
      this.$api
        .changeImageDuration(tvspotId, duration)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'entryUpdatedSuccess',
            translate: true,
          });

          this.showChangeImageDurationDialog = false;
          this.imageDuration = null;

          this.fetchContent().then(() => {
            this.loading = false;
          });
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
    },
    refreshContentDate(ctype, spotId) {
      this.loading = true;
      this.$api.refreshContentDate(ctype, spotId).then(() => {
        this.$bus.$emit('alert-success', {
          message: 'entryUpdatedSuccess',
          translate: true,
        });
        this.showRefreshContentDateDialog = false;
        this.fetchContent().then(() => {
          this.loading = false;
        });
      });
    },
    changeCategory(ctype, spotId, categoryId) {
      this.loading = true;
      this.$api.changeContentCategory(ctype, spotId, categoryId).then(() => {
        this.$bus.$emit('alert-success', {
          message: 'entryUpdatedSuccess',
          translate: true,
        });
        this.fetchContent().then(() => {
          this.loading = false;
        });
      });
    },
    setInitialWrapperExpansion() {
      if (this.numEntriesStores < this.expandEntriesIfNumStoresLessThan) {
        this.$nextTick(() => {
          this.expandAllEntries();
        });
      }
    },
    refetchOnContentProcessed() {
      this.$bus.$on('refetch-on-content-processed', (content) => {
        // This listener may still be active, but we have navigated
        // elsewhere. Ignore the signal, since this component may be
        // deconstructed by the garbage collector right now.
        if (this.$route.name !== 'spot') {
          return;
        }
        if (
          this.isContentSameAs(content, this.spot) ||
          this.isContentSameAs(content, this.bundle)
        ) {
          this.$api
            .retrieveContent(content.ctype, content.id)
            .then((response) => {
              this.spot = response.data;
              this.updateSpotLastFetched();
              this.showContentProcessedNotification(content);
            });
        }
      });
    },
    handleInitialEditOverlayDataDialogOpened() {
      if (
        this.$route.query &&
        urlQueryParamToBoolean(this.$route.query.overlayediting)
      ) {
        window.scroll(0, document.body.scrollHeight / 2);
        this.onEntrySelectionOverlayToggled('overlayediting');
        this.onEntrySelectionOverlayConfirmed();

        removeUrlQueryParamsFromCurrentRoute();
      }
    },
    fetchCategoriesIfNeeded() {
      if (this.categories.length > 0) {
        return;
      }
      this.$store.dispatch('LIST_CATEGORIES');
    },
  },
};
</script>

<style scoped>
.menu-container {
  display: flex;
  align-items: baseline;
}
</style>



// WEBPACK FOOTER //
// src/pages/SpotDetail.vue